import { h } from '../../../src/ui/dom';
import { byWayOf, tripMonths, whereNow } from '../copy';
import { drawTrack } from '../map';
import { beenHref, placeHref, placesHref, publicNav } from '../nav';
import { destinationBySlug, planByDestination, type DestinationPlan } from '../places/destinations';
import { placeBySlug, type Photo } from '../places/places';
import { buildTimeline } from '../timeline';
import { VOYAGE } from '../voyage/voyage';
import { getJson, isoDay, liveLines, span, type PlanData, type RouteView, type TrackView } from '../view';
import { VISITS_URL } from './places';

const MONTH = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAME = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthYear = (iso: string) => `${MONTH_NAME[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

let stopLines = () => {};


const photoImg = (ph: Photo, cls: string, eager = false) => h('img', { class: cls, src: ph.file, alt: ph.alt, loading: eager ? 'eager' : 'lazy' });

function comingCard(p: DestinationPlan, n: number): HTMLElement {
  return h('a', { class: 'coming-card', href: placeHref(p.dest.slug) },
    p.dest.hero ? photoImg(p.dest.hero, 'cc-img') : null,
    h('div', { class: 'cc-scrim' }),
    h('div', { class: 'cc-text' },
      h('span', { class: 'cc-when' }, `${String(n).padStart(2, '0')} · ${span(p.from, p.to)}`),
      h('h3', {}, p.dest.name),
      h('p', {}, p.dest.tagline)));
}

function timelineBox(pages: DestinationPlan[], today: string): HTMLElement {
  const t = buildTimeline(pages, today, p => span(p.from, p.to));
  const ended = new Set(pages.filter(pg => pg.to < today).map(pg => pg.dest.slug));
  const years: { year: string; n: number }[] = [];
  for (const m of t.months) {
    const y = m.slice(0, 4), last = years[years.length - 1];
    if (last?.year === y) last.n++; else years.push({ year: y, n: 1 });
  }
  const cells: HTMLElement[] = [];
  let col = 2;
  for (const y of years) { cells.push(h('span', { class: 'tl-year', style: `grid-row: 1; grid-column: ${col} / span ${y.n}` }, y.year)); col += y.n; }
  t.months.forEach((m, i) => cells.push(h('span', { class: 'tl-month', style: `grid-row: 2; grid-column: ${i + 2}` }, MONTH[Number(m.slice(5, 7)) - 1]!)));
  const rows = t.rows.map((r, i) => h('div', { class: 'tl-row', style: `grid-row: ${i + 3}` },
    h('a', { href: placeHref(r.dest.slug) }, r.dest.name),
    h('span', { class: `tl-bar${ended.has(r.dest.slug) ? ' past' : ''}`, style: `grid-column: ${r.from + 2} / ${r.to + 3}`, title: r.dates })));
  const now = t.now === null ? null : h('span', { class: 'tl-now', style: `grid-column: ${t.now + 2}; grid-row: 2 / ${t.rows.length + 3}`, title: 'This month' });
  return h('div', { class: 'timeline', role: 'group', 'aria-label': 'The plan month by month' },
    h('div', { class: 'tl-grid', style: `--months: ${t.months.length}` }, ...cells, ...rows, ...(now ? [now] : [])));
}

function feature(p: DestinationPlan, n: number, flip: boolean): HTMLElement | null {
  const f = p.dest.feature;
  if (!f) return null;
  return h('section', { class: `feature${flip ? ' flip' : ''}` },
    photoImg(f.photo, 'feature-img'),
    h('div', { class: 'feature-scrim' }),
    h('div', { class: 'feature-inner' },
      h('span', { class: 'feature-eyebrow' }, `Chapter ${String(n).padStart(2, '0')} · ${p.dest.name}`),
      h('h2', {}, f.headline),
      h('p', {}, `${f.text} ${span(p.from, p.to)}.`),
      h('a', { href: placeHref(p.dest.slug) }, 'Read the chapter →')));
}

/** Photo credits the Commons licences ask for, for every Commons photo on the page. */
function credits(photos: Photo[]): HTMLElement | null {
  const seen = new Set<string>(), list = photos.filter(ph => ph.license && !seen.has(ph.file) && seen.add(ph.file));
  if (!list.length) return null;
  return h('p', { class: 'landing-credits' }, 'Photos: ', ...list.flatMap((ph, i) => [
    i ? ' · ' : '', `${ph.credit}, `, h('a', { href: ph.licenseUrl, rel: 'license noopener', target: '_blank' }, ph.license), ' (', h('a', { href: ph.source, rel: 'noopener', target: '_blank' }, 'Commons'), ')']));
}

/**
 * The home page: a full-bleed photo with the trip and where the boat is, the next
 * countries, the route chart and the month-by-month timeline, feature chapters and how to come and visit.
 * Everything about the trip comes from the live plan.
 */
export async function renderLanding(app: HTMLElement): Promise<void> {
  const [track, route, visits] = await Promise.all([
    getJson<TrackView>('/api/track/public'),
    getJson<RouteView>('/api/route'),
    getJson<PlanData>(VISITS_URL),
  ]);
  const today = todayIso();
  const plan = visits.ok ? visits.data : null;
  const pages = plan ? planByDestination(plan.stops, plan.windows).pages : [];
  const first = plan?.stops[0], last = plan?.stops[plan.stops.length - 1];
  const heroPhoto = destinationBySlug.get('palau')!.hero!;
  const stops = route.ok ? route.data.stops : [];
  const months = first && last ? tripMonths(first.depart, last.arrive ?? last.depart) : 0;
  const via = byWayOf(pages);
  const used: Photo[] = [heroPhoto];

  const nav = publicNav('home');
  const where = track.ok ? whereNow(track.data, stops, Date.now()) : null;
  const chip = where ? h('div', { class: 'now-chip' },
    h('span', { class: 'now-dot', 'aria-hidden': 'true' }),
    h('div', {}, h('span', { class: 'now-label' }, where.label), h('span', { class: 'now-text' }, where.text), where.sub ? h('span', { class: 'now-sub' }, where.sub) : null)) : null;
  const hero = h('section', { class: 'hero-full' },
    photoImg(heroPhoto, 'hero-full-img', true),
    h('div', { class: 'hero-full-scrim' }),
    h('div', { class: 'hero-full-inner' },
      h('div', { class: 'hero-full-top' }, h('a', { class: 'brand', href: '/' }, 'SV COS'), nav),
      h('div', { class: 'hero-full-bottom' },
        h('div', { class: 'hero-full-copy' },
          h('p', { class: 'hero-full-eyebrow' }, `Helia 44 catamaran · sailing since ${VOYAGE.since}`),
          h('h1', {}, first && last ? `${first.label} to ${last.label}, the slow way.` : 'SV COS'),
          months ? h('p', { class: 'hero-full-lede' }, `Carolina, Olivia and Steve have been sailing since ${VOYAGE.since}, from Miami through the Bahamas and the Caribbean, the Panama Canal and across the Pacific to New Caledonia. Next: ${months} months${via ? ` by way of ${via}` : ''}. No rush. Come find us.`) : null,
          plan && first && last ? h('p', { class: 'hero-full-sub', id: 'trip' }, `${isoDay(first.depart)} to ${isoDay(last.arrive ?? last.depart)} · ${pages.length} countries · ${plan.stops.length - 1} stops`) : null,
          h('div', { class: 'hero-full-actions' },
            h('a', { class: 'pill accent', href: '#visit' }, 'Come and visit'),
            h('a', { class: 'pill ghost', href: '#route' }, 'Follow along'))),
        chip)));

  const body = h('div', { class: 'landing-body' });
  if (!visits.ok) body.append(h('p', { class: visits.status === 404 ? 'notice' : 'err' }, visits.status === 404 ? 'No plan published yet. Check back soon.' : `The plan could not load: ${visits.error}`));

  const upcoming = pages.filter(p => p.to >= today).slice(0, 3);
  if (upcoming.length) {
    upcoming.forEach(p => p.dest.hero && used.push(p.dest.hero));
    body.append(h('section', { class: 'landing-sec' },
      h('div', { class: 'landing-head' }, h('h2', {}, "Where we'll be next"), h('a', { href: placesHref() }, `All ${pages.length} countries →`)),
      h('div', { class: 'coming-grid' }, ...upcoming.map(p => comingCard(p, pages.indexOf(p) + 1)))));
  }

  if (plan && first && last) body.append(h('section', { class: 'landing-sec stats' },
    ...[[pages.length, 'countries'], [plan.stops.length - 1, 'stops'], [months, 'months'], [3, 'guest cabins']]
      .map(([n, label]) => h('div', { class: 'stat' }, h('span', { class: 'stat-n' }, String(n)), h('span', { class: 'stat-l' }, String(label))))));

  body.append(h('section', { class: 'landing-sec so-far', 'aria-labelledby': 'so-far-h' },
    h('div', { class: 'so-far-copy' },
      h('p', { class: 'feature-eyebrow' }, 'So far'),
      h('h2', { id: 'so-far-h' }, `Miami to Nouméa, since ${VOYAGE.since}`),
      h('p', {}, `About ${VOYAGE.nm.toLocaleString('en-US')} nautical miles and ${VOYAGE.stoppedIn} countries and territories: three winters in the Bahamas, the Caribbean from the Virgin Islands to Grenada, the Panama Canal, 31 days across the Pacific, then the Marquesas, the Tuamotus, Bora Bora and Fiji.`),
      h('a', { class: 'pill ghost', href: beenHref() }, 'See where we’ve been'))));

  body.append(h('section', { class: 'landing-sec why', 'aria-labelledby': 'why-h' },
    h('h2', { id: 'why-h' }, 'Why slowly'),
    h('div', {},
      h('p', {}, "Cyclone seasons, visas and the best anchorages set our pace, not a finish line. We'd rather spend a month in one lagoon than a day in thirty."),
      h('p', {}, 'So every date below is our best plan, and the weather gets the final say.'))));

  const routeSec = h('section', { class: 'landing-sec', id: 'route', 'aria-labelledby': 'route-h' },
    h('div', { class: 'landing-head' }, h('h2', { id: 'route-h' }, "Where we've been, where we're going")),
    h('p', { class: 'landing-lede' }, 'The orange line is our track and the dashed line our plan; tap a stop to read about it. Our position updates whenever COS is online.'));
  stopLines();
  if (track.ok) {
    const chart = h('div', { class: 'landing-chart' });
    drawTrack(chart, track.data, { plan: route.ok ? route.data : null });
    const lines = h('div', { class: 'status-lines' });
    stopLines = liveLines(lines, track.data, l => h('p', {}, l));
    routeSec.append(chart, lines);
  } else routeSec.append(h('p', { class: 'err' }, track.error));
  if (!route.ok && route.status !== 404) routeSec.append(h('p', { class: 'err' }, `The planned route could not load: ${route.error}`));
  if (pages.length) routeSec.append(h('h3', { class: 'landing-h3' }, 'Month by month'), timelineBox(pages, today));
  body.append(routeSec);

  const features = pages.filter(p => p.dest.feature);
  features.forEach(p => used.push(p.dest.feature!.photo));
  const featureEls = features.map((p, i) => feature(p, pages.indexOf(p) + 1, i % 2 === 1)).filter((el): el is HTMLElement => el !== null);

  const windows = pages.flatMap(p => p.windows.filter(w => w.to >= today).map(w => ({ p, w })));
  const seen = new Set<string>();
  const visitList = windows.filter(({ w }) => !seen.has(w.key) && seen.add(w.key)).slice(0, 4);
  const boat = { file: '/boat/at-anchor.jpg', alt: 'COS at anchor in turquoise water' };
  const visit = h('section', { class: 'landing-sec visit', id: 'visit', 'aria-labelledby': 'visit-h' },
    h('img', { class: 'visit-img', src: boat.file, alt: boat.alt, loading: 'lazy' }),
    h('div', { class: 'visit-copy' },
      h('h2', { id: 'visit-h' }, 'Fly in for a week. Stay for a month.'),
      h('p', {}, 'Every stay of a week or more near an airport is a chance to join us. Pick one and we\'ll meet you there. Dates move with the weather, so check with us before you book.'),
      visitList.length ? h('ul', { class: 'visit-windows' }, ...visitList.map(({ p, w }) => h('li', {},
        h('a', { href: `${placeHref(p.dest.slug)}#w-${w.key}` }, p.dest.name),
        h('span', {}, `${w.stops.length > 1 ? `${w.stops[0]} to ${w.stops[w.stops.length - 1]}` : w.stops[0] ?? ''} · ${w.airport ? `fly to ${w.airport.iata}` : 'no airport nearby'} · ${monthYear(w.from)}${monthYear(w.from) === monthYear(w.to) ? '' : ` – ${monthYear(w.to)}`}`)))) : null,
      h('a', { class: 'pill accent', href: placesHref() }, 'See where’s next')));

  const footer = h('footer', { class: 'landing-foot' },
    h('span', { class: 'landing-foot-brand' }, `SV COS · Helia 44${track.ok && track.data.region ? ` · somewhere in ${track.data.region}` : ''}`),
    h('span', {}, 'Our position updates whenever COS is online.'),
    credits(used));

  app.replaceChildren(h('div', { class: 'landing' }, hero, body, ...featureEls, h('div', { class: 'landing-body' }, visit), footer));
}

let hiddenAt = 0;
/** Coming back to a tab left open for more than a minute: draw the page again with fresh data. */
export function refreshOnReturn(app: HTMLElement): void {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { hiddenAt = Date.now(); return; }
    if (hiddenAt && Date.now() - hiddenAt > 60_000) void renderLanding(app);
  });
}
